import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data.user);
      toast.success("Welcome back.");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <section className="wrap flex min-h-[70vh] items-center justify-center py-16">
      <form
        onSubmit={submit}
        noValidate
        className="card-surface w-full max-w-md !p-8"
        data-testid="admin-login-form"
      >
        <p className="eyebrow">Staff sign-in</p>
        <h1 className="font-display text-3xl font-extrabold">Admin portal</h1>
        <p className="text-sm text-muted-foreground">
          Authorized Kamalama staff only. Sign-ups, messages and vendor
          sign-ups are private.
        </p>
        <div className="flex flex-col gap-1.5">
          <label className="label" htmlFor="a-email">Email</label>
          <input id="a-email" className="field" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="label" htmlFor="a-password">Password</label>
          <input id="a-password" className="field" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
        </div>
        <button className="btn w-fit" type="submit" disabled={busy} data-testid="admin-login-button">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </section>
  );
}

const COLUMNS = {
  membership: ["Name", "Email", "Phone", "Town / island", "Can help with", "Received"],
  contact: ["Name", "Email", "Topic", "Message", "Received"],
  vendor: ["Business — contact", "Email", "Phone", "Category", "Booth notes", "Received"],
};

function Submissions({ type }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    setItems(null);
    api
      .get(`/admin/submissions?type=${type}`)
      .then((r) => setItems(r.data.items))
      .catch(() => setItems([]));
  }, [type]);

  if (items === null)
    return <p className="py-10 text-muted-foreground">Loading…</p>;
  if (items.length === 0)
    return (
      <p className="py-10 text-muted-foreground" data-testid="admin-empty-state">
        No submissions yet.
      </p>
    );

  const cell = (it, col) => {
    switch (col) {
      case "Business — contact":
        return `${it.business_name} — ${it.contact_name}`;
      case "Name":
        return it.name;
      case "Email":
        return it.email;
      case "Phone":
        return it.phone || "—";
      case "Town / island":
        return it.town || "—";
      case "Can help with":
        return (it.areas || []).join(", ") || "—";
      case "Topic":
        return it.topic;
      case "Message":
        return it.message;
      case "Category":
        return it.category;
      case "Booth notes":
        return [it.description, it.notes].filter(Boolean).join(" · ") || "—";
      case "Received":
        return new Date(it.created_at).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      default:
        return "";
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="ledger-table min-w-[720px]" data-testid="admin-submissions-table">
        <thead>
          <tr>
            {COLUMNS[type].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} data-testid="admin-submission-row">
              {COLUMNS[type].map((h) => (
                <td
                  key={h}
                  className={
                    h === "Received"
                      ? "!whitespace-nowrap"
                      : "!whitespace-normal max-w-md !text-left"
                  }
                >
                  {cell(it, h)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsForm() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) =>
      setForm({
        donation_url: r.data.donation_url || "",
        resources_updated: r.data.resources_updated || "",
        stage: String(r.data.stage ?? 2),
        ledger: r.data.ledger || { date: "", raised: "", expenses: "" },
        updates: (r.data.updates || []).join("\n"),
      }),
    );
  }, []);

  if (!form) return <p className="py-10 text-muted-foreground">Loading…</p>;

  const setLedger = (k) => (e) =>
    setForm((f) => ({ ...f, ledger: { ...f.ledger, [k]: e.target.value } }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      donation_url: form.donation_url,
      resources_updated: form.resources_updated,
      stage: Number(form.stage),
      updates: form.updates.split("\n"),
      clear_ledger: !form.ledger.date,
    };
    if (form.ledger.date) {
      payload.ledger = {
        date: form.ledger.date,
        raised: parseFloat(form.ledger.raised) || 0,
        expenses: parseFloat(form.ledger.expenses) || 0,
      };
    }
    try {
      await api.put("/admin/settings", payload);
      await refresh();
      toast.success("Settings saved. The site updates immediately.");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={save} className="flex max-w-xl flex-col gap-4" data-testid="admin-settings-form">
      <div className="flex flex-col gap-1.5">
        <label className="label" htmlFor="s-donation">Donation page URL</label>
        <input id="s-donation" className="field" type="url" placeholder="https://… (leave empty while giving is not open)" value={form.donation_url} onChange={(e) => setForm((f) => ({ ...f, donation_url: e.target.value }))} data-testid="admin-donation-url-input" />
        <p className="text-xs text-muted-foreground">
          When set, every Donate button opens this link in a new tab. When
          empty, Donate shows "online giving opens soon" with the contact
          details.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="label" htmlFor="s-updated">Resources "Last updated" date</label>
        <input id="s-updated" className="field" type="text" placeholder="e.g. October 1, 2026" value={form.resources_updated} onChange={(e) => setForm((f) => ({ ...f, resources_updated: e.target.value }))} data-testid="admin-resources-updated-input" />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="label" htmlFor="s-stage">Program stage</label>
        <select id="s-stage" className="field" value={form.stage} onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))} data-testid="admin-stage-select">
          <option value="1">Stage 1 — Immediate Community Response</option>
          <option value="2">Stage 2 — Business Needs Assessment</option>
          <option value="3">Stage 3 — Application, Verification & Awards</option>
        </select>
      </div>
      <fieldset className="rounded-xl border border-border p-4">
        <legend className="px-1 text-sm font-bold">Sunday figures</legend>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="label" htmlFor="s-ldate">Update date (empty = not posted yet)</label>
            <input id="s-ldate" className="field" type="text" placeholder="e.g. October 4, 2026" value={form.ledger.date} onChange={setLedger("date")} data-testid="admin-ledger-date-input" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="label" htmlFor="s-lraised">Total funds raised ($)</label>
              <input id="s-lraised" className="field" type="number" step="0.01" min="0" value={form.ledger.raised} onChange={setLedger("raised")} data-testid="admin-ledger-raised-input" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="label" htmlFor="s-lextotal">Expenses ($)</label>
              <input id="s-lextotal" className="field" type="number" step="0.01" min="0" value={form.ledger.expenses} onChange={setLedger("expenses")} data-testid="admin-ledger-expenses-input" />
            </div>
          </div>
        </div>
      </fieldset>
      <div className="flex flex-col gap-1.5">
        <label className="label" htmlFor="s-updates">Program updates (one per line)</label>
        <textarea id="s-updates" className="field min-h-[120px]" value={form.updates} onChange={(e) => setForm((f) => ({ ...f, updates: e.target.value }))} data-testid="admin-updates-input" />
      </div>
      <button className="btn w-fit" type="submit" data-testid="admin-settings-save">
        Save settings
      </button>
    </form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("membership");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined)
    return (
      <section className="wrap flex min-h-[60vh] items-center justify-center text-muted-foreground">
        Checking session…
      </section>
    );

  if (user === null) return <Login onLogin={setUser} />;

  const TABS = [
    ["membership", "Membership sign-ups"],
    ["vendor", "Vendor sign-ups"],
    ["contact", "Contact messages"],
    ["settings", "Site settings"],
  ];

  return (
    <section className="wrap py-14 sm:py-16" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Admin portal</p>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">
            Program Lead console
          </h1>
          <p className="text-sm text-muted-foreground">Signed in as {user.email}</p>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={async () => {
            await api.post("/auth/logout").catch(() => {});
            setUser(null);
          }}
          data-testid="admin-logout-button"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto border-b border-border" role="tablist" data-testid="admin-tabs">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px whitespace-nowrap rounded-t-lg border-b-2 px-4 py-3 text-sm font-bold transition ${
              tab === id
                ? "border-terra text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`admin-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="py-8">
        {tab !== "settings" ? (
          <Submissions type={tab} />
        ) : (
          <SettingsForm />
        )}
      </div>
    </section>
  );
}
